import {Injectable} from '@angular/core';
import {MapLayersManager} from '../map-layers-manager';
import { LineLayerSpecification } from 'maplibre-gl';
import {RouteConfigurationWithSchema, RouteHierarchyItem} from '../../../models/route';
import {RoutesManagerService} from '../../../../data/routes/routes-manager.service';
import {RouteSourceService} from './route-source.service';
import {MapFilters} from '../../../../configuration/map-filters';
import { RoutesLayerService } from './routes-layer.service';
import { LineStyle } from '../../../models/styling';

/*
    This service displays custom map layer:
    MapLayerType.ROUTE
    which displays data created by route import
 */
@Injectable({
    providedIn: 'root'
})
export class StaticRoutesLayerService {

    static readonly LAYER_ID_STATIC_ROUTES_PREFIX = 'static-routes-layer';

    private mapLayersManager: MapLayersManager;
    private lineLayers: LineLayerSpecification[] = [];
    private routes: RouteHierarchyItem[];
    private routeConfigurations: RouteConfigurationWithSchema[];

    private visibleRouteConfigIds: number[] = [];

    constructor(private routesManager: RoutesManagerService,
    ) { }

    init(mapLayersManager: MapLayersManager) {
        if (!!this.mapLayersManager) {
            throw Error('The map layers manager has already been set.');
        }
        this.mapLayersManager = mapLayersManager;
        this.routeConfigurations = this.routesManager.getRouteConfigurations();
        this.routes = this.routesManager
            .getRouteHierarchyLeaves()
            .filter(it => !it.hidden);
    }

    release() {
        if (!this.mapLayersManager) {
            throw Error('The map has not been set!');
        }

        this.mapLayersManager = null;
        this.lineLayers = [];
        this.visibleRouteConfigIds = [];
        this.routes = undefined;
        this.routeConfigurations = undefined;
    }

    // on map style change
    reset() {
        this.lineLayers = [];
        this.visibleRouteConfigIds = [];
    }

    addRouteConfigLayers(routeConfigurationId: number, isVisible: boolean) {
        if (isVisible) {
            this.visibleRouteConfigIds.push(routeConfigurationId);
        }
        this.addLayers(routeConfigurationId, isVisible);
    }

    setRouteConfigLayersVisibility(routeConfigurationId: number, isVisible: boolean) {
        // update visible field
        if (isVisible) {
            if (!this.visibleRouteConfigIds.includes(routeConfigurationId)) {
                this.visibleRouteConfigIds.push(routeConfigurationId);
            }
        } else {
            const visibleIndex = this.visibleRouteConfigIds.indexOf(routeConfigurationId);
            if (visibleIndex >= 0) {
                this.visibleRouteConfigIds.splice(visibleIndex, 1);
            }
        }

        this.lineLayers
            .filter(layer => layer.id.startsWith(`${StaticRoutesLayerService.LAYER_ID_STATIC_ROUTES_PREFIX}_${routeConfigurationId}_`))
            .forEach(lineLayer => {
                this.mapLayersManager.setLayerVisibility(lineLayer.id, isVisible);
            });
    }

    private addLayers(routeConfigurationId: number, isVisible: boolean) {

        this.getLineLayers(routeConfigurationId).forEach(layer => {
            layer.layout.visibility = isVisible ? 'visible' : 'none';
            this.lineLayers.push(layer);
            this.mapLayersManager.addLayer(layer, this.mapLayersManager.firstSymbolLayerId);
        });
    }

    private getLineLayers(routeConfigurationId: number): LineLayerSpecification[] {
        return this.routes
            .filter(route => route.configId === routeConfigurationId)
            .map(route => {
                const routeConfigurationWSchema = this.routeConfigurations.find(config => config.id === route.configId);
                if (!routeConfigurationWSchema) {
                  console.error('Missing route configuration. This should not happen!');
                }
                let globalRouteStyle = routeConfigurationWSchema?.schema?.style;
                if (!globalRouteStyle) {
                    console.warn('Missing schema style. This should not happen!');
                    globalRouteStyle = new LineStyle('#FF0000', 4, 1.0);
                }

                const widthExpression = RoutesLayerService.getWidthExpression(
                  routeConfigurationWSchema,
                  globalRouteStyle.width,
                  this.routes
                );
                return {
                  id: `${StaticRoutesLayerService.LAYER_ID_STATIC_ROUTES_PREFIX}_${route.configId}_${route.routeId}`,
                  type: 'line',
                  source: RouteSourceService.ROUTES_SOURCE_ID,
                  filter: MapFilters.getRoutesFilter(route.configId, route.routeId, null, null),
                  layout: {
                    'line-join': 'miter',
                    'line-cap': 'butt',
                    'line-miter-limit': 2,
                  },
                  paint: {
                    'line-color': RoutesLayerService.getColorExpression(
                      routeConfigurationWSchema,
                      globalRouteStyle.color,
                      this.routes
                    ),
                    'line-width': [
                      'interpolate',
                      ['exponential', 2],
                      ['zoom'],
                      10,
                      ['*', widthExpression, ['^', 2, -1]],
                      24,
                      ['*', widthExpression, ['^', 2, 8]],
                    ],
                    'line-opacity': RoutesLayerService.getOpacityExpression(
                      routeConfigurationWSchema,
                      globalRouteStyle.opacity,
                      this.routes
                    ),
                    'line-dasharray': RoutesLayerService.getLineTypeExpression(
                      routeConfigurationWSchema,
                      globalRouteStyle.lineType,
                      this.routes
                    ),
                  },
                } as LineLayerSpecification;
        });
    }
}
